import { PageContainer } from "../../components/pageContainer/PageContainer";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ReactElement, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { SpinnerLegacy } from "../../components";
import { Alert } from "../../components/alert/Alert";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import {
  DELIVERY_SCHEDULE,
  DELIVERY_METHOD_CHARGES,
  DELIVERY_ZONE,
  DELIVERY_SCHEDULES,
  UPDATE_DELIVERY_AREA,
} from "../../constants/routes";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useDeliveryArea } from "../../hooks/api/v2/deliveryArea/useDeliveryArea";
import { useDeliveryAreaUpdate } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaUpdate";
import { useDeliveryAreaCreateContext } from "../../hooks/useDeliveryAreaCreateContext.shared";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { DeliveryAreaForm } from "./DeliveryAreaForm";
import { DeliveryAreaFormRef } from "./DeliveryAreaForm.shared";
import { useFocusedNavigation } from "../../hooks/useFocusedNavigation";

dayjs.extend(utc);

export function DeliveryAreaUpdate(): ReactElement {
  const navigate = useNavigate();
  const deliveryScheduleId = useParamAsNumber(
    DELIVERY_SCHEDULE,
    "deliveryAreaId",
    false,
  );
  const legacyDeliveryAreaId = useParamAsNumber(
    UPDATE_DELIVERY_AREA,
    "deliveryAreaId",
    false,
  );
  const deliveryAreaId = deliveryScheduleId ?? legacyDeliveryAreaId;
  if (!deliveryAreaId) throw new Error("No id found");

  const { state } = useLocation();
  const redirectToNewDeliverySchedule =
    state?.redirectToNewDeliverySchedule ?? null;
  const fromCustomerZoneId: number | null = state?.fromCustomerZoneId ?? null;

  const deliveryArea = useDeliveryArea(deliveryAreaId);
  const { postcodes, selectedDays } = useDeliveryAreaCreateContext();

  const [postcodeError, setPostcodeError] = useState<boolean>(false);

  const remoteFeatureFlags = useRemoteFeatureFlags();
  const unbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  useFocusedNavigation(!unbundlingEnabled);

  const formRef = useRef<DeliveryAreaFormRef>(null);

  const updateDeliveryAreaMutation = useDeliveryAreaUpdate(deliveryAreaId);

  return (
    <>
      <NavigationBar
        title={
          unbundlingEnabled ? deliveryArea.data?.name : "Update delivery area"
        }
        leftIcon={"CLOSE"}
        onClickLeftIcon={() => {
          if (unbundlingEnabled) {
            if (formRef.current?.isDirty()) {
              formRef.current?.submitForm();
            } else if (fromCustomerZoneId !== null) {
              navigate(
                DELIVERY_ZONE.href({ deliveryZoneId: fromCustomerZoneId }),
              );
            } else {
              navigate(DELIVERY_SCHEDULES);
            }
            return;
          }
          if (fromCustomerZoneId !== null) {
            navigate(
              DELIVERY_ZONE.href({ deliveryZoneId: fromCustomerZoneId }),
            );
          } else if (redirectToNewDeliverySchedule) {
            navigate(DELIVERY_SCHEDULES);
          } else {
            navigate(DELIVERY_METHOD_CHARGES.href({ method: "direct" }));
          }
        }}
      />
      <PageContainer className={"flex flex-col gap-4"}>
        {updateDeliveryAreaMutation.isPending || deliveryArea.isLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            {updateDeliveryAreaMutation.isError && (
              <Alert
                type={"error"}
                message={
                  apiV2AssertError(updateDeliveryAreaMutation.error)?.response
                    ?.data.message ?? "An unknown error occurred"
                }
              />
            )}
            <DeliveryAreaForm
              ref={formRef}
              hideActions={unbundlingEnabled}
              onSubmit={(data) => {
                if (!postcodes.length) return setPostcodeError(true);
                setPostcodeError(false);

                updateDeliveryAreaMutation.mutate(
                  {
                    startAt: dayjs(data.startAt).toDate(),
                    name: data.name,
                    postcodes,
                    weeklyCadence: parseInt(
                      data.weeklyCadence?.toString() ?? "",
                    ),
                    days: selectedDays,
                    orderCutOffTime: data.orderCutOffTime
                      ? dayjs
                          .utc(data.orderCutOffTime)
                          .set("hour", dayjs(data.orderCutOffTime).hour())
                          .toDate()
                      : null,
                    orderCutOffDaysBefore: data.orderCutOffDaysBefore,
                    includeWeekend: data.includeWeekend,
                  },
                  {
                    onSuccess: () => {
                      if (fromCustomerZoneId !== null) {
                        navigate(
                          DELIVERY_ZONE.href({
                            deliveryZoneId: fromCustomerZoneId,
                          }),
                        );
                      } else if (unbundlingEnabled) {
                        navigate(DELIVERY_SCHEDULES);
                      } else {
                        navigate(
                          DELIVERY_METHOD_CHARGES.href({ method: "direct" }),
                        );
                      }
                    },
                  },
                );
              }}
              postcodeError={postcodeError}
              deliveryArea={deliveryArea.data}
            />
          </>
        )}
      </PageContainer>
    </>
  );
}
